// ─── Family Finance domain types ─────────────────────────────
// Shared between the dashboard and the family-finance service API.

export type AccountType =
  | 'checking'
  | 'savings'
  | 'brokerage'
  | 'retirement'
  | 'credit'
  | 'mortgage'
  | '529'
  | 'hsa'
  | 'property'

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Checking',
  savings: 'Savings',
  brokerage: 'Brokerage',
  retirement: 'Retirement (IRA/401k)',
  '529': '529',
  hsa: 'HSA',
  credit: 'Credit card',
  mortgage: 'Mortgage / loan',
  property: 'Property / other asset',
}

// The kind-of-account axis, which is how the owner actually reads the list:
// "what do I have at the bank, what is invested, what do I owe". Mortgages sit
// apart from credit cards even though both are debts — a card balance turns
// over monthly, a mortgage is a fixed obligation you look at on its own terms.
export type AccountCategory = 'bank' | 'investment' | 'credit' | 'mortgage' | 'property'

export const ACCOUNT_CATEGORY_ORDER: AccountCategory[] = [
  'bank',
  'investment',
  'credit',
  'mortgage',
  'property',
]

export const ACCOUNT_CATEGORY_LABELS: Record<AccountCategory, string> = {
  bank: 'Bank accounts',
  investment: 'Investment accounts',
  credit: 'Credit cards',
  mortgage: 'Mortgages & loans',
  property: 'Property & other assets',
}

export const ACCOUNT_CATEGORY_ICONS: Record<AccountCategory, string> = {
  bank: '🏦',
  investment: '📈',
  credit: '💳',
  mortgage: '🏠',
  property: '🧱',
}

const CATEGORY_OF_TYPE: Record<AccountType, AccountCategory> = {
  checking: 'bank',
  savings: 'bank',
  brokerage: 'investment',
  retirement: 'investment',
  '529': 'investment',
  hsa: 'investment',
  credit: 'credit',
  mortgage: 'mortgage',
  property: 'property',
}

export function categoryOf(type: AccountType): AccountCategory {
  return CATEGORY_OF_TYPE[type] ?? 'bank'
}

/** Account types whose balances belong in the Investments view. */
export const INVESTMENT_TYPES: AccountType[] = ['brokerage', 'retirement', '529', 'hsa']

export function isInvestmentAccount(account: { type: AccountType }): boolean {
  return categoryOf(account.type) === 'investment'
}

export type TaxTreatment = 'taxable' | 'traditional' | 'roth' | '529' | 'hsa' | 'none'

export const TAX_TREATMENT_LABELS: Record<TaxTreatment, string> = {
  taxable: 'Taxable',
  traditional: 'Tax-deferred (Traditional)',
  roth: 'Tax-free (Roth)',
  '529': '529 (education)',
  hsa: 'HSA (triple-advantaged)',
  none: '—',
}

export type ConnectionProvider = 'simplefin' | 'plaid' | 'manual'

// Personal vs. business is the split that matters at tax time — a business
// account's balance belongs to an entity's books, not to household net worth.
export type AccountUse = 'personal' | 'business'

export const ACCOUNT_USE_LABELS: Record<AccountUse, string> = {
  personal: 'Personal',
  business: 'Business',
}

export interface Institution {
  id: string
  name: string
  icon: string
  status: 'connected' | 'error' | 'pending' | 'manual'
  connectionType: 'linked' | 'manual'
  provider?: ConnectionProvider
  lastSync: string | null
}

export interface FinancialAccount {
  id: string
  institutionId: string
  institutionName: string
  name: string
  mask: string
  type: AccountType
  taxTreatment: TaxTreatment
  owner: string // 'Nathanael' | 'Meagan' | 'Joint' | entity name
  balance: number // negative = liability
  use: AccountUse
  entityId: string | null // set when use === 'business' and the entity is known
  provider?: ConnectionProvider
  lastUpdated: string
  /** Set once the owner corrects the type, so a sync stops re-guessing it. */
  typeSource?: 'owner' | null
  // Payment terms for cards and loans. SimpleFIN authorizes balances only and
  // almost never carries these, so they are owner-entered — and they are what
  // turns a card balance into a bill with a due date.
  minimumPayment?: number | null
  dueDay?: number | null // day of the month, 1–31
  autopay?: boolean
}

export interface PaymentTerms {
  minimumPayment: number | null
  dueDay: number | null
  autopay: boolean
}

/** Accounts that carry a recurring payment: cards and loans. */
export function isPayableAccount(account: { type: AccountType }): boolean {
  const category = categoryOf(account.type)
  return category === 'credit' || category === 'mortgage'
}

/**
 * Next calendar date landing on `day`.
 *
 * Clamped to the length of the target month, so a card due on the 31st is due
 * the 28th in February rather than silently rolling into March.
 */
export function nextDueDate(day: number, from: Date = new Date()): string {
  const target = Math.min(Math.max(Math.round(day), 1), 31)
  const candidate = new Date(from.getFullYear(), from.getMonth(), 1)
  if (from.getDate() >= target) candidate.setMonth(candidate.getMonth() + 1)
  const lastOfMonth = new Date(candidate.getFullYear(), candidate.getMonth() + 1, 0).getDate()
  candidate.setDate(Math.min(target, lastOfMonth))
  const y = candidate.getFullYear()
  const m = String(candidate.getMonth() + 1).padStart(2, '0')
  const d = String(candidate.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export interface ManualAccountInput {
  name: string
  institutionName: string
  type: AccountType
  taxTreatment: TaxTreatment
  owner: string
  balance: number
  use: AccountUse
  entityId: string | null
}

export interface Holding {
  id: string
  accountId: string
  accountName: string
  symbol: string
  name: string
  quantity: number
  price: number
  value: number
  costBasis: number
  taxTreatment: TaxTreatment
  assetClass: 'stock' | 'etf' | 'mutual-fund' | 'bond' | 'money-market'
}

export interface BudgetCategory {
  id: string
  name: string
  icon: string
  budgeted: number
  spent: number
}

export interface MonthlyBudget {
  month: string // YYYY-MM
  categories: BudgetCategory[]
}

export type BillStatus = 'upcoming' | 'due-soon' | 'overdue' | 'paid'

export interface Bill {
  id: string
  name: string
  payee: string
  amount: number
  nextDueDate: string // YYYY-MM-DD
  frequency: 'monthly' | 'quarterly' | 'annual' | 'one-time'
  autopay: boolean
  linkedAccountId: string | null
  linkedAccountName: string | null
  category: string
  reminderDaysBefore: number
  emailReminder: boolean
  lastPaid: string | null
}

export type TaxDocType =
  | 'W-2'
  | '1099-NEC'
  | '1099-INT'
  | '1099-DIV'
  | '1099-B'
  | '1099-K'
  | '1099-MISC'
  | 'K-1'
  | '1098'
  | '5498'
  | 'Receipt'
  | 'Prior Return'
  | 'Other'

// ─── Documents ───────────────────────────────────────────────
// One store, three top-level folders. Uploads are routed to a folder by
// filename inference (see lib/document-router) and the owner can override
// the destination before committing the upload.

export type DocumentFolder = 'taxes' | 'businesses' | 'accounts'

export const FOLDER_LABELS: Record<DocumentFolder, string> = {
  taxes: 'Taxes',
  businesses: 'Businesses',
  accounts: 'Accounts',
}

export const FOLDER_ICONS: Record<DocumentFolder, string> = {
  taxes: '🧾',
  businesses: '🏛️',
  accounts: '🏦',
}

export type BusinessDocType =
  | 'Formation'
  | 'Operating Agreement'
  | 'EIN Letter'
  | 'Franchise Tax / PIR'
  | 'Contract'
  | 'Invoice'
  | 'Payroll'
  | 'Ledger / P&L'
  | 'Insurance'
  | 'License / Permit'
  | 'Minutes / Resolution'
  | 'Other'

export type AccountDocType =
  | 'Statement'
  | 'Trade Confirmation'
  | 'Loan Document'
  | 'Closing Statement'
  | 'Beneficiary Form'
  | 'Insurance'
  | 'Other'

export type DocumentCategory = TaxDocType | BusinessDocType | AccountDocType

export const FOLDER_CATEGORIES: Record<DocumentFolder, DocumentCategory[]> = {
  taxes: [
    'W-2', '1099-NEC', '1099-INT', '1099-DIV', '1099-B', '1099-K', '1099-MISC',
    'K-1', '1098', '5498', 'Receipt', 'Prior Return', 'Other',
  ],
  businesses: [
    'Formation', 'Operating Agreement', 'EIN Letter', 'Franchise Tax / PIR',
    'Contract', 'Invoice', 'Payroll', 'Ledger / P&L', 'Insurance',
    'License / Permit', 'Minutes / Resolution', 'Other',
  ],
  accounts: [
    'Statement', 'Trade Confirmation', 'Loan Document', 'Closing Statement',
    'Beneficiary Form', 'Insurance', 'Other',
  ],
}

export interface FinanceDocument {
  id: string
  folder: DocumentFolder
  category: DocumentCategory
  name: string // display name, editable
  fileName: string // original filename as uploaded
  mimeType: string
  sizeKb: number
  taxYear: number | null // set for taxes/, optional elsewhere
  entityId: string | null // set for businesses/
  accountId: string | null // set for accounts/
  source: string // institution, entity, or who sent it
  notes: string
  uploadedAt: string
  sharedWithCpa: boolean
  hasFile: boolean // bytes are retrievable (local store or backend)
  storageKey: string | null // backend object key once the sync API is live
}

export interface DocumentMeta {
  folder: DocumentFolder
  category: DocumentCategory
  name: string
  taxYear: number | null
  entityId: string | null
  accountId: string | null
  source: string
  notes: string
}

export interface CpaShare {
  id: string
  email: string
  name: string
  taxYears: number[]
  status: 'invited' | 'active' | 'revoked'
  invitedAt: string
  expiresAt: string
  lastAccess: string | null
}

// ─── Business entities ───────────────────────────────────────

export type EntityClassification =
  | 'partnership' // Form 1065, K-1s
  | 's-corp' // Form 1120-S
  | 'c-corp' // Form 1120
  | 'disregarded' // Schedule C with 1040

export const CLASSIFICATION_LABELS: Record<EntityClassification, string> = {
  partnership: 'Partnership (Form 1065)',
  's-corp': 'S corporation (Form 1120-S)',
  'c-corp': 'C corporation (Form 1120)',
  disregarded: 'Disregarded / Schedule C',
}

export interface BusinessEntity {
  id: string
  name: string
  shortName: string
  state: string
  classification: EntityClassification
  classificationAssumed: boolean // defaulted by the app, owner should confirm
  members: string[]
  description: string
  icon: string
  ein: string // '' until the owner fills it in
  formedOn: string | null // YYYY-MM-DD
  registeredAgent: string
}

export interface EntityInput {
  name: string
  shortName: string
  state: string
  classification: EntityClassification
  members: string[]
  description: string
  icon: string
  ein: string
  formedOn: string | null
  registeredAgent: string
}

// Ledger of things that happen to an entity but aren't a P&L line or a
// document — contributions, distributions, resolutions, filing notes.
export type EntityRecordKind =
  | 'note'
  | 'filing'
  | 'resolution'
  | 'contract'
  | 'asset'
  | 'contribution'
  | 'distribution'
  | 'other'

export const RECORD_KIND_LABELS: Record<EntityRecordKind, string> = {
  note: 'Note',
  filing: 'Filing',
  resolution: 'Resolution',
  contract: 'Contract',
  asset: 'Asset',
  contribution: 'Capital contribution',
  distribution: 'Distribution',
  other: 'Other',
}

export interface EntityRecord {
  id: string
  entityId: string
  kind: EntityRecordKind
  title: string
  date: string // YYYY-MM-DD
  amount: number | null
  notes: string
  createdAt: string
}

export interface EntityRecordInput {
  entityId: string
  kind: EntityRecordKind
  title: string
  date: string
  amount: number | null
  notes: string
}

export interface PnLLine {
  category: string
  amount: number
}

export interface EntityPnL {
  entityId: string
  year: number
  through: string // YYYY-MM-DD data cutoff
  revenue: number
  expenses: PnLLine[]
  netIncome: number
}

export interface Form1099Record {
  id: string
  entityId: string
  direction: 'issued' | 'received'
  type: '1099-NEC' | '1099-MISC' | '1099-K' | '1099-INT' | '1099-DIV'
  counterparty: string
  amount: number
  taxYear: number
  status: 'draft' | 'needs-w9' | 'filed' | 'received'
}

export type Form1099Input = Omit<Form1099Record, 'id'>

export type ReturnStatus =
  | 'not-started'
  | 'in-progress'
  | 'extension-filed'
  | 'filed'
  | 'accepted'

export interface ReturnScheduleItem {
  id: string
  entityId: string | null // null = personal
  entityName: string
  form: string
  jurisdiction: 'Federal' | 'Texas' | 'Delaware' | string
  taxYear: number
  dueDate: string
  extendedDueDate: string | null
  status: ReturnStatus
  notes: string | null
}

// ─── Real property ───────────────────────────────────────────
// The owner types a street address; the service resolves it against two
// systems the household already runs — the Easy Tax Protest CAD database for
// physical facts, and the Ironrock Properties partner API for a sale CMA.

/** Physical facts from the county appraisal district roll. */
export interface PropertyFacts {
  sqft: number | null
  yearBuilt: number | null
  bedrooms: number | null
  bathsFull: number | null
  bathsHalf: number | null
  lotSizeSqft: number | null
  garageSpaces: number | null
  countyId: number | null
  parcelAccount: string | null
  situsAddress: string | null
  // Roll values for the latest tax year the CAD database holds.
  marketValue: number | null
  appraisedValue: number | null
  assessedValue: number | null
  /** The value on the year's Notice of Appraised Value (= market value). */
  noticedValue: number | null
  taxYear: number | null
}

/**
 * 'cma' is Ironrock's segmented Sale CMA; 'lease' its Lease CMA quote (a
 * monthly rent — never a property value). 'cad-noticed' / 'cad-assessed' are
 * the CAD roll's noticed and assessed figures via Easy Tax Protest; 'cad' is
 * the legacy single roll entry older refreshes stored.
 */
export type ValuationSource =
  | 'cma'
  | 'lease'
  | 'cad'
  | 'cad-noticed'
  | 'cad-assessed'
  | 'avm'
  | 'manual'

/** Each valuation names where its number came from — shown on its chip. */
export const VALUATION_SOURCE_LABELS: Record<ValuationSource, string> = {
  cma: 'Sale CMA · Ironrock',
  lease: 'Lease CMA · Ironrock',
  cad: 'CAD roll · Easy Tax Protest',
  'cad-noticed': 'Tax noticed · Easy Tax Protest',
  'cad-assessed': 'Tax assessed · Easy Tax Protest',
  avm: 'AVM · RentCast',
  manual: 'Manual',
}

export interface PropertyValuation {
  source: ValuationSource
  value: number
  asOf: string
  /** Range around the point estimate (AVM low/high, CMA sold-bucket range). */
  low?: number | null
  high?: number | null
  /** 'monthly' on the lease quote — the value is a rent, not a price. */
  period?: 'monthly' | null
  photoUrl?: string | null
  compCount?: number | null
  /**
   * Segmented market census behind a CMA. New sale CMAs report
   * active / activeContingent / pending / sold; older snapshots carry the
   * combined contingent bucket instead (lease: active / contingent / leased).
   */
  statusCounts?: Record<string, number> | null
  /** Lease quote built from thin data — indicative rather than well-supported. */
  thinSample?: boolean | null
  radiusMiles?: number | null
  subjectSource?: string | null
  reportUrl?: string | null
  pdfUrl?: string | null
  disclaimer?: string | null
  attribution?: string | null
  appraisedValue?: number | null
  assessedValue?: number | null
  taxYear?: number | null
}

/**
 * How each upstream answered on the last refresh. Kept per-source and shown in
 * the UI on purpose: a property with no CMA because Ironrock couldn't match the
 * address is a different thing from a property that isn't worth anything.
 */
export type SourceStatus = 'ok' | 'not-found' | 'error' | 'not-configured'

export interface SourceResult {
  status: SourceStatus
  at: string
  message: string | null
}

export interface Property {
  id: string
  /** Front photo from the MLS listing, supplied with the CMA. */
  photoUrl?: string | null
  label: string
  address: string
  city: string
  zip: string
  county: string
  owner: string
  entityId: string | null
  linkedMortgageAccountId: string | null
  purchasePrice: number | null
  purchaseDate: string | null
  homestead: boolean
  /** Owner-supplied living area, used when the CAD has no record of the house. */
  livingAreaSqft: number | null
  notes: string
  facts?: PropertyFacts
  valuations?: PropertyValuation[]
  sources?: Partial<Record<'cad' | 'cma' | 'avm' | 'store', SourceResult>>
  lastRefresh?: string | null
  createdAt?: string
}

export interface PropertyInput {
  label: string
  address: string
  city: string
  zip: string
  owner: string
  entityId: string | null
  linkedMortgageAccountId: string | null
  purchasePrice: number | null
  purchaseDate: string | null
  homestead: boolean
  livingAreaSqft: number | null
  notes: string
}

/**
 * The number to show for a property. CMA first — it prices the house against
 * what comparable houses actually sold for, which is the question "what is this
 * worth" means; the CAD roll is a tax figure that habitually lags the market.
 * The lease quote is deliberately absent: a monthly rent must never stand in
 * for — or sum into — a property value.
 */
export function workingValue(property: Property): PropertyValuation | null {
  const valuations = property.valuations ?? []
  const order: ValuationSource[] = [
    'manual',
    'cma',
    'avm',
    'cad-noticed',
    'cad',
    'cad-assessed',
  ]
  for (const source of order) {
    const hit = valuations.find((v) => v.source === source && v.value > 0)
    if (hit) return hit
  }
  return null
}

// ─── Summary ─────────────────────────────────────────────────

export interface FinanceSummary {
  netWorth: number
  totalAssets: number
  totalLiabilities: number
  netWorthChange30d: number
  investableAssets: number
  billsDueNext14Days: { count: number; total: number }
  budgetMonth: string
  budgetSpent: number
  budgetTotal: number
  lastSync: string | null
}
